import { CountryType, CountryZod } from '@fd/common/api/fetchCountryList';
import { z } from 'zod';
import { create } from 'zustand';
import { static_countries } from '../model/static_countries';
import { ProxyTypeName, ProxyTypeZodEnum } from '@fd/common/biz/proxyType';
import { persist } from 'zustand/middleware';
import { ProxyPoolRotationSchema, ProxyPoolRotationType } from '@fd/common/biz/proxyPoolRotationOptions';
import { ProxyPoolProtocolSchema, ProxyPoolProtocolType } from '@fd/common/biz/proxyPoolProtocolOptions';
import { createShallowStoreHook } from './createShallowStoreHook';

const ProxyPoolCreationFormZod = z.object({
  name: z.string(),
  proxyType: ProxyTypeZodEnum,
  country: CountryZod.optional(),
  cities: z.array(z.string()),
  quantity: z.number().int(),
  isCreateAfterPayment: z.boolean(),
  rotation: ProxyPoolRotationSchema,
  protocol: ProxyPoolProtocolSchema
});

type ProxyPoolCreationFormType = z.infer<typeof ProxyPoolCreationFormZod>;

type ProxyPoolCreationStoreType = ProxyPoolCreationFormType & {
  setCountry: (country?: CountryType) => void;
  setCities: (cities: string[]) => void;
  setName: (name: string) => void;
  setProxyType: (proxyType: ProxyTypeName) => void;
  setQuantity: (quantity: number) => void;
  setCreateAfterPayment: (isCreateAfterPayment: boolean) => void;
  setRotation: (rotation: ProxyPoolRotationType) => void;
  setProtocol: (protocol: ProxyPoolProtocolType) => void;
  resetForm: () => void;
};

const initialState: ProxyPoolCreationFormType = {
  country: static_countries[0],
  cities: [],
  name: 'Default proxy pool',
  proxyType: 'residential',
  quantity: 10,
  isCreateAfterPayment: false,
  rotation: 0,
  protocol: 'http'
};

const useProxyPoolCreationFormStore = create<ProxyPoolCreationStoreType>()(
  persist(
    (set) => ({
      ...initialState,
      setCountry: (country?: CountryType) =>
        set((state) => {
          if (state.country?.countryCode !== country?.countryCode)
            return {
              ...state,
              country: country,
              cities: [],
            };
          else return state;
        }),
      setCities: (cities) => set({ cities }),
      setName: (name) => set({ name }),
      setProxyType: (proxyType) => set({ proxyType }),
      setQuantity: (quantity) => set({ quantity }),
      setCreateAfterPayment: (isCreateAfterPayment) =>
        set({ isCreateAfterPayment }),
      setRotation: (rotation) => set ({rotation}),
      setProtocol: (protocol) => set ({protocol}),
      resetForm: () => set(initialState),
    }),
    {
      name: 'proxy-pool-creation-form-storage',
    }
  )
);

const useProxyPoolCreationForm = createShallowStoreHook(useProxyPoolCreationFormStore);

export default useProxyPoolCreationForm;
